import { motion } from 'framer-motion';
import { CheckCircle2, Rocket, TrendingUp, AlertTriangle, FileEdit, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import type { SuperAdminBucket, SuperAdminBucketKPI } from '../../../services/dashboardService';

const BUCKET_VISUAL: Record<SuperAdminBucket, {
  icon: typeof CheckCircle2;
  bg: string;
  text: string;
  ring: string;
  bar: string;
  gradient: string;
}> = {
  completed: {
    icon: CheckCircle2,
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    ring: 'ring-emerald-500/30',
    bar: 'bg-emerald-500',
    gradient: 'from-emerald-500/15 via-transparent to-transparent'
  },
  readyToStart: {
    icon: Rocket,
    bg: 'bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    ring: 'ring-blue-500/30',
    bar: 'bg-blue-500',
    gradient: 'from-blue-500/15 via-transparent to-transparent'
  },
  onTrack: {
    icon: TrendingUp,
    bg: 'bg-teal-500/10',
    text: 'text-teal-600 dark:text-teal-400',
    ring: 'ring-teal-500/30',
    bar: 'bg-teal-500',
    gradient: 'from-teal-500/15 via-transparent to-transparent'
  },
  atRisk: {
    icon: AlertTriangle,
    bg: 'bg-rose-500/10',
    text: 'text-rose-600 dark:text-rose-400',
    ring: 'ring-rose-500/30',
    bar: 'bg-rose-500',
    gradient: 'from-rose-500/15 via-transparent to-transparent'
  },
  draftIncomplete: {
    icon: FileEdit,
    bg: 'bg-slate-500/10',
    text: 'text-slate-600 dark:text-slate-400',
    ring: 'ring-slate-500/30',
    bar: 'bg-slate-500',
    gradient: 'from-slate-500/10 via-transparent to-transparent'
  }
};

function Sparkline({ values, colorClass }: { values: number[]; colorClass: string }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  const w = 100;
  const h = 28;
  const stepX = w / Math.max(values.length - 1, 1);
  const points = values
    .map((v, i) => `${i * stepX},${h - (v / max) * (h - 4) - 2}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-7 w-full" preserveAspectRatio="none">
      <polyline
        points={points}
        fill="none"
        strokeWidth={1.5}
        className={colorClass}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface Props {
  kpi: SuperAdminBucketKPI;
  active?: boolean;
  index?: number;
  onClick?: (bucket: SuperAdminBucket) => void;
}

export default function BucketCard({ kpi, active, index = 0, onClick }: Props) {
  const visual = BUCKET_VISUAL[kpi.bucket];
  const Icon = visual.icon;

  const TrendIcon =
    kpi.trend === 'up' ? ArrowUpRight :
    kpi.trend === 'down' ? ArrowDownRight :
    Minus;
  const trendColor =
    kpi.trend === 'up' ? 'text-emerald-600 dark:text-emerald-400'
    : kpi.trend === 'down' ? 'text-rose-600 dark:text-rose-400'
    : 'text-slate-500';

  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(kpi.bucket)}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative w-full overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:bg-slate-800/60 ${
        active
          ? `border-transparent ring-2 ${visual.ring}`
          : 'border-slate-200/80 dark:border-slate-700/50'
      }`}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${visual.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />

      <div className="relative flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${visual.bg}`}>
          <Icon className={`h-5 w-5 ${visual.text}`} />
        </div>
        <div className={`flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold dark:bg-slate-700 ${trendColor}`}>
          <TrendIcon className="h-3 w-3" />
          {Math.abs(kpi.delta).toFixed(0)}%
        </div>
      </div>

      <p className="relative mt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
        {kpi.label}
      </p>
      <p className="relative mt-1 text-3xl font-bold tabular-nums text-slate-900 dark:text-white">
        {kpi.value.toLocaleString()}
      </p>
      <p className="relative mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
        {kpi.description}
      </p>

      <div className={`relative mt-3 ${visual.text}`}>
        <Sparkline values={kpi.spark} colorClass={visual.text} />
      </div>

      <div className={`pointer-events-none absolute bottom-0 left-0 h-[3px] w-full ${visual.bar} origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100`} />
    </motion.button>
  );
}
